import { Button } from 'antd';
import React, { FC } from 'react';

import { getPermissionErrorMessage, SubPermissions, User } from '@/model/AccountModel';
import { AlarmAcknowledgementActions, AlarmActionOptions } from '@/model/AlarmModel';
import { AcknowledgeIcon, ClearIcon, OperatorNoteIcon, PreviewBadgeIcon } from '@/resources/Icons';
import cx from 'classnames';
import styles from './individualActionButtons.module.scss';

type Props = {
	handleAction: (option: AlarmAcknowledgementActions | AlarmActionOptions) => Promise<void>;
	isAcknowledgedAlarm: boolean;
	isAlarmSelected: boolean;
	showPreviewBadge?: boolean;
	onlyIcons?: boolean;
	disableAllButtons?: boolean;
	style?: React.CSSProperties;
};

const user: User = getUser();
const isAcknowledgedAlarmAllowed: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmsRTNs_Acknowledge).allowed;
const isClearAlarmAllowed: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmsRTNs_ClearinStandardMode).allowed;
const isAddNoteAllowed: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmViewer_AddNotes).allowed;
const isPreviewBadgeAllowed: boolean = User.getSubComponentPermission(user, SubPermissions.Badges_Badges_Preview).allowed;
const { isClearUnacknowledgedAllowed } = user;

const IndividualActionButtons: FC<Props> = ({
	handleAction,
	isAcknowledgedAlarm,
	isAlarmSelected,
	showPreviewBadge = false,
	onlyIcons = false,
	disableAllButtons = false,
	style = {},
}) => {
	const isAckSelectedOptionDisabled = disableAllButtons || (!isAlarmSelected && isAcknowledgedAlarmAllowed) || !isAcknowledgedAlarmAllowed;
	const isClearAlarmOptionDisabled =
		disableAllButtons || (!isAcknowledgedAlarm && !isClearUnacknowledgedAllowed) || (!isAlarmSelected && isClearAlarmAllowed) || !isClearAlarmAllowed;
	const isAddOperatorNoteDisabled = disableAllButtons || (!isAlarmSelected && isAddNoteAllowed) || !isAddNoteAllowed;
	const isPreviewBadgeDisabled = disableAllButtons || (!isAlarmSelected && isPreviewBadgeAllowed) || !isPreviewBadgeAllowed;

	const ackTitle: string = getPermissionErrorMessage(isAcknowledgedAlarmAllowed) || _('Acknowledge');
	const previewBadgeTitle: string = getPermissionErrorMessage(isPreviewBadgeAllowed) || _('PreviewBadge');
	const clearTitle: string =
		!isAcknowledgedAlarm && !isClearUnacknowledgedAllowed
			? _('RequireAcknowledgeBeforeClearing')
			: getPermissionErrorMessage(isClearAlarmAllowed) || _('Clear');
	const noteTitle: string = getPermissionErrorMessage(isAddNoteAllowed) || _('AddOperatorNote');

	return (
		<div className={cx(styles.buttonsContainer, { [styles.onlyIcons]: onlyIcons })} style={style}>
			{!isAcknowledgedAlarm && (
				<Button
					className={styles.ack}
					disabled={isAckSelectedOptionDisabled}
					htmlType='button'
					icon={<AcknowledgeIcon />}
					key='ack'
					onClick={() => {
						handleAction?.(AlarmAcknowledgementActions.AcknowledgedOne);
					}}
					size={onlyIcons ? 'small' : 'middle'}
					title={ackTitle}>
					{!onlyIcons ? _('Acknowledge') : null}
				</Button>
			)}
			<Button
				className={styles.clear}
				disabled={isClearAlarmOptionDisabled}
				htmlType='button'
				icon={<ClearIcon />}
				key='clear'
				onClick={() => {
					handleAction?.(isAcknowledgedAlarm ? AlarmAcknowledgementActions.ClearOneAcknowledged : AlarmAcknowledgementActions.ClearOneUnacknowledged);
				}}
				size={onlyIcons ? 'small' : 'middle'}
				title={clearTitle}>
				{!onlyIcons ? _('Clear') : null}
			</Button>
			<Button
				className={styles.note}
				disabled={isAddOperatorNoteDisabled}
				htmlType='button'
				icon={<OperatorNoteIcon />}
				key='note'
				onClick={() => {
					handleAction?.(AlarmActionOptions.ADD_OPERATOR_NOTE);
				}}
				size={onlyIcons ? 'small' : 'middle'}
				title={noteTitle}>
				{!onlyIcons ? _('AddOperatorNote') : null}
			</Button>
			{showPreviewBadge && (
				<Button
					className={styles.preview}
					disabled={isPreviewBadgeDisabled}
					htmlType='button'
					icon={<PreviewBadgeIcon />}
					key='preview'
					onClick={() => {
						handleAction?.(AlarmActionOptions.DISPLAY_CREDENTIALS);
					}}
					size={onlyIcons ? 'small' : 'middle'}
					title={previewBadgeTitle}>
					{!onlyIcons ? _('PreviewBadge') : null}
				</Button>
			)}
		</div>
	);
};

export { IndividualActionButtons };
